import logo from "@assets/logo.png";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AppleSignInButton } from "@web/components/AppleSignInButton";
import { GoogleSignInButton } from "@web/components/GoogleSignInButton";
import { Button, Field } from "@web/components/ui";
import { useToast } from "@/contexts";
import { getProviderConnections, hasConnectedProvider } from "@/features/sync/connections";
import { useSignInForm } from "@/hooks/useSignInForm";

export default function SignIn() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/sign-in" }) as { redirect?: string };
  const toast = useToast();

  async function continueAfterAuth(): Promise<void> {
    const connections = await getProviderConnections();
    const nextPath = search.redirect ?? "/dashboard";
    if (hasConnectedProvider(connections)) {
      navigate({ to: nextPath, replace: true });
    } else {
      navigate({
        to: "/health-setup",
        search: { redirect: nextPath },
        replace: true,
      });
    }
  }

  const {
    email,
    setEmail,
    password,
    setPassword,
    submitting,
    formError,
    handlePasswordSignIn,
    handleAppleSignIn,
  } = useSignInForm(() => {
    void continueAfterAuth();
  });

  function onGoogleSignInSuccess(): void {
    void continueAfterAuth();
  }

  function onGoogleSignInError(error: string): void {
    toast.showAlert("Sign-in failed", error, "error");
  }

  async function onAppleSignIn(): Promise<void> {
    if (search.redirect) {
      localStorage.setItem("signin_redirect", search.redirect);
    }
    await handleAppleSignIn();
    if (formError) toast.showAlert("Sign-in failed", formError, "error");
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-[26rem] bg-card rounded-[1.25rem] border border-border shadow-[1px_1px_0px_0px_#1c1b2e] p-6 sm:p-8 flex flex-col gap-5">
        <div className="flex flex-col items-center gap-3 text-center">
          <img src={logo} alt="FootSlapping" className="w-16 h-16 object-contain" />
          <div>
            <h1 className="text-text text-2xl font-extrabold tracking-tight">Welcome back</h1>
            <p className="text-text-muted text-sm mt-1">Sign in to compete with friends</p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <GoogleSignInButton
            text="signin_with"
            width="100%"
            onSuccess={onGoogleSignInSuccess}
            onError={onGoogleSignInError}
          />
          <AppleSignInButton onPress={onAppleSignIn} disabled={submitting} />
        </div>

        <div className="relative flex items-center">
          <div className="flex-1 border-t border-border-light" />
          <span className="mx-3 text-text-muted text-xs">or</span>
          <div className="flex-1 border-t border-border-light" />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handlePasswordSignIn();
          }}
          className="flex flex-col gap-4"
        >
          <Field
            label="Email"
            value={email}
            onChangeText={setEmail}
            placeholder="you@example.com"
            type="email"
          />
          <Field
            label="Password"
            value={password}
            onChangeText={setPassword}
            placeholder="Enter password"
            secureTextEntry
          />
          {formError ? (
            <p className="text-coral text-xs font-semibold" role="alert">
              {formError}
            </p>
          ) : null}
          <Button
            type="submit"
            label={submitting ? "Signing in..." : "Sign in"}
            disabled={submitting}
          />
        </form>

        <p className="text-text-muted text-xs text-center">
          Don't have an account?{" "}
          <Link
            to="/sign-up"
            search={{ redirect: search.redirect }}
            className="text-aqua font-extrabold no-underline hover:underline"
          >
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
